import { toast } from "react-toastify";

import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import {
  userPlanSelector,
  userGetInventoryTypeSelector,
  effectiveUserTimeZoneSelector,
  isSupportUserSessionSelector,
  isImpersonationSessionSelector,
  featureFlagSelector,
} from "src/store/system/selector";
import { getMercariIntegrations } from "src/store/plaidIntegration/selector";
import { mercariIntegrationCreate } from "src/apiService/modules/mercari";
import {
  ingestMappedPendingInventoryFile,
  deletePendingInventoryByListingIds,
} from "src/apiService/modules/pendingInventory";
import {
  getMercariUser,
  RequiredExtensionVersion,
} from "src/utils/extension/mercari";
import type { MercariIntegration } from "src/interfaces/plaidIntegration.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import parallelize from "src/utils/parallelize";
import Cacher from "src/utils/Cacher";
import FirebaseBatcher from "src/utils/FirebaseBatcher";
import { stampNormalizedMatchFields } from "src/utils/normalizeMatchKey";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";
import { moveSaleApi } from "src/apiService/modules/sales";
import type { Sale } from "src/interfaces/sale.interface";
import { uploadFile } from "src/utils/storage";
import {
  endOfMonthInTimeZone,
  startOfMonthInTimeZone,
} from "src/utils/timezone";

import type { Dispatcher, SyncStatus, ErrorSyncStatusCode } from "../../types";
import { updateSyncStatus as _updateSyncStatus } from "../actions";
import {
  saveSyncDate,
  getLastSyncDate,
  setBeforeUnloadHandler,
  clearBeforeUnloadHandler,
  ReSyncInterval,
  RunningKeys,
  isUserAllowedToDirectImport,
  shouldRunDailySync,
  checkIfItIsInitialImport,
  checkExtensionOrDispatchError,
  notifySyncAlreadyLocked,
} from "../utils";
import { withSyncLock, type SyncLock } from "../syncLock";

import { mapInventory, mapSale, CompleteOrderStates } from "./mappings";
import { finalizeSaleComputedFields } from "src/utils/sales";
import MercariClient, {
  NotLoggedError,
  UnauthorizedError,
  type UserItemsQueryParameters,
} from "./MercariClient";
import { recordEventWithCheck } from "src/apiService/modules/events";

const ParalelizeJobs = 10;
const AllowedTradingStates = [...CompleteOrderStates, "not_shipped"];

async function updateIntegration(integrationId: string) {
  try {
    const data = await getMercariUser();

    if (data.cookies && data.username) {
      await mercariIntegrationCreate({
        integrationId,
        cookies: data.cookies,
        username: data.username,
      });
      recordEventWithCheck({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-mercari",
      });
    }
  } catch (err) {
    console.error("There was an error:", err);
  }
}

function updateSyncStatus(status: SyncStatus) {
  return _updateSyncStatus({
    integration: "mercari" as const,
    status,
  });
}

async function syncSale(
  uid: string,
  cache: Cacher<string, boolean>,
  client: MercariClient,
  transformed: ReturnType<typeof mapSale>[number],
  file: string,
  lock: SyncLock
) {
  lock.throwIfAborted();
  const db = firestore();

  const transactionId = transformed.transaction_id;

  if (transactionId) {
    const isEmpty = await cache.run(
      transactionId,
      async (transactionId: string) => {
        const snapshot = await db
          .collection("Sales")
          .where("user", "==", uid)
          .where("transaction_id", "==", transactionId)
          .get();
        return snapshot.empty;
      }
    );

    if (!isEmpty) return;
  }

  const saleDoc = db.collection("Sales").doc();
  await saleDoc.set(
    finalizeSaleComputedFields({
      ...transformed,
      id: saleDoc.id,
      user: uid,
      unreviewed: true,
      file,
      import_type: "daily-sync",
      import_platform: "mercari",
      import_date: new Date(),
    })
  );

  if (transformed.sale_price_check_error) {
    const errorDoc = db.collection("Error_Alerts").doc();
    await errorDoc.set({
      id: errorDoc.id,
      user: uid,
      sale_id: saleDoc.id,
      new: true,
      triageStatus: "untriaged",
      error: "mercari-sale-price-check-error",
      createdAt: new Date(),
      message:
        "There was an error importing some of your Mercari data. Please reach out to support@myresellergenie.com so we can help resolve this issue.",
      error_message:
        "Mercari sale failed the sale price consistency check during import.",
      platform: "Mercari",
      type: "general-error-notification-form-submission",
    });
  }
}

async function syncSalesByStatus(
  uid: string,
  cache: Cacher<string, boolean>,
  client: MercariClient,
  allSales: any[],
  lastDate: string,
  filename: string,
  bankAccount: ReturnType<typeof bankAccountByIntegrationIdSelector>,
  lock: SyncLock,
  status?: UserItemsQueryParameters["status"]
) {
  const args: UserItemsQueryParameters = {
    sortBy: "updated",
  };

  if (status) args.status = status;

  for await (const { sales } of client.userItemsQueryFull(args)) {
    let stop = false;
    allSales.push(...sales);
    await parallelize(
      sales,
      async (sale: (typeof sales)[number]) => {
        lock.throwIfAborted();
        // Sales that are part of a bundle are also listed, we'll have to ignore them
        // Sales that are part of a bundle won't have this property
        // 2025-01-15: regular sales won't have the `transactionEvidence` property,
        //             but they will have the `activeOrder` one
        if (!sale.transactionEvidence && !sale.activeOrder) return;

        const updated = new Date(sale.updated * 1000).toISOString();
        // We only want to import sales that have been updated after the last sync date
        if (updated < lastDate) {
          stop = true;
          return;
        }

        const [orderStatus, product] = await Promise.all([
          client.orderStatusConsolidated(sale.id),
          client.productQuery(sale.id).catch((e) => null),
        ]);

        if ((orderStatus?.orderState || "").startsWith("canceled")) return;

        if (
          status !== "trading" &&
          !AllowedTradingStates.includes(orderStatus.orderState)
        )
          return;

        if (sale.itemType === "bundle") {
          const orderContent = orderStatus?.componentContents?.find(
            (c) => c.type === "orderContent" && c.orderContent
          )?.orderContent;

          if (orderContent?.items?.length)
            sale.products = (
              await Promise.all(
                orderContent.items.map((i) =>
                  client.productQuery(i.id).catch((e) => null)
                )
              )
            ).filter(Boolean);
        }

        sale.orderStatus = orderStatus;
        if (product) sale.product = product;

        for (const transformed of mapSale(sale)) {
          if (!transformed.sale_date) return;
          // XXX: we won't filter sales by the sale date, we will rely in the previous update date filter
          //      In order to prevent duplicates, syncSales checks if the transaction_id already exists

          if (bankAccount) {
            transformed.sale_platform_account_id = bankAccount.id;
            transformed.sale_platform = bankAccount.name;
          }

          await syncSale(uid, cache, client, transformed, filename, lock);
        }
      },
      ParalelizeJobs
    );

    if (stop) break;
  }
}

async function syncInProcess(
  uid: string,
  client: MercariClient,
  lock: SyncLock
) {
  const db = firestore();
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .get();
  if (snapshot.empty) return;

  for (const doc of snapshot.docs) {
    lock.throwIfAborted();
    const data = doc.data();
    if (!data.transaction_id) continue;
    if (
      !data.import_platform ||
      data.import_platform?.toLowerCase() !== "mercari"
    )
      continue;
    if (data.is_return) {
      // Returns shouldn't be marked as "in process"
      // Update that flag and ignore it
      console.warn(
        "mercari sales daily sync found an in process return (in process)",
        {
          id: doc.id,
          data,
        }
      );

      await doc.ref.update({
        in_process: false,
        in_process_date: new Date(),
      });
      continue;
    }

    try {
      const orderStatus = await client.orderStatusConsolidated(
        data.transaction_id
      );
      const orderState = orderStatus.orderState || "";
      if (CompleteOrderStates.includes(orderState)) {
        await db.collection("Sales").doc(data.id).update({
          in_process: false,
          in_process_date: new Date(),
        });
      } else if (orderState.startsWith("canceled")) {
        await moveSaleApi(data as Sale, { allowZeroCostDelete: true });
      }
    } catch (error) {
      console.trace(
        error,
        `id: ${data?.id}`,
        `trasaction id: ${data?.transaction_id}`
      );
    }
  }
}

async function syncSales(
  client: MercariClient,
  integration: MercariIntegration,
  date: Date,
  bankAccount: ReturnType<typeof bankAccountByIntegrationIdSelector>,
  lock: SyncLock
): Promise<void> {
  const timestamp = date.valueOf();
  const uid = await getUserId();
  const lastDate = getLastSyncDate(
    integration,
    "mercariLastSyncDateTransactions"
  );
  const allSales: any[] = [];
  const filename = `${uid}/mercariSales/${timestamp}_${integration.id}.json`;
  const cache = new Cacher<string, boolean>();

  await syncSalesByStatus(
    uid,
    cache,
    client,
    allSales,
    lastDate,
    filename,
    bankAccount,
    lock
  );
  await syncSalesByStatus(
    uid,
    cache,
    client,
    allSales,
    lastDate,
    filename,
    bankAccount,
    lock,
    "trading"
  );
  await syncInProcess(uid, client, lock);

  await uploadFile(filename, JSON.stringify(allSales));
}

async function syncInventory(
  client: MercariClient,
  integration: MercariIntegration,
  date: Date,
  lock: SyncLock,
  pendingInventoryEnabled: boolean
): Promise<void> {
  const db = firestore();
  const lastDate = getLastSyncDate(integration, "mercariLastSyncDateInventory");
  const uid = await getUserId();
  const its = [] as ReturnType<typeof mapInventory>[];

  for await (const { sales: items } of client.userItemsQueryFull({
    status: "on_sale",
    sortBy: "created",
    sortType: "desc",
  })) {
    lock.throwIfAborted();
    let stop = false;
    await parallelize(
      items,
      async (item: (typeof items)[number]) => {
        lock.throwIfAborted();
        const product = await client.productQuery(item.id);
        if (product.status !== "on_sale") return;
        const inventory = mapInventory(product);
        if (
          !inventory ||
          (inventory.list_date && inventory.list_date.toISOString() < lastDate)
        ) {
          stop = true;
          return;
        }
        its.push(inventory);
      },
      ParalelizeJobs
    );

    if (stop) break;
  }

  if (its.length) {
    lock.throwIfAborted();
    const timestamp = date.valueOf();
    const filename = `${uid}/mercariInventory/${timestamp}_${integration.id}.json`;
    await uploadFile(filename, JSON.stringify(its));
    if (pendingInventoryEnabled) {
      await ingestMappedPendingInventoryFile({
        filename,
        import_platform: "mercari",
        integrationId: integration.id,
        timestamp,
      });
    } else {
      const doc = db.collection("Downloads").doc();
      await doc.set({
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        filename,
        new: true,
        timestamp,
        type: "mercariInventory",
        user: uid,
        integrationId: integration.id,
      });
    }
  }
}

function syncDaily(integrationId: string, inventory: boolean) {
  return async (dispatch, getState): Promise<boolean> => {
    const started = await withSyncLock(
      integrationId,
      RunningKeys,
      async (lock) => {
        const db = firestore();
        const start = new Date();
        const getIntegration = () => {
          const integration = getMercariIntegrations(getState()).find(
            ({ id }) => id === integrationId
          );

          if (!integration)
            throw Error(`Integration not found ${integrationId}`);

          return integration;
        };
        const client = new MercariClient();

        const cookieUserId = (await client.getCredentials()).userId;
        if (cookieUserId !== getIntegration().uid) throw new NotLoggedError();

        const initialize = await client.initialize();
        if (initialize?.user?.userId !== getIntegration().uid)
          throw new NotLoggedError();

        await updateIntegration(integrationId);

        const bankAccount = bankAccountByIntegrationIdSelector(
          getState(),
          integrationId
        );

        // Sales
        dispatch(updateSyncStatus({ type: "syncing", step: "sales" }));
        await syncSales(client, getIntegration(), start, bankAccount, lock);

        lock.throwIfAborted();
        await saveSyncDate(
          integrationId,
          "mercariLastSyncDateTransactions",
          start.toISOString()
        );

        if (inventory) {
          dispatch(updateSyncStatus({ type: "syncing", step: "inventory" }));
          await syncInventory(
            client,
            getIntegration(),
            start,
            lock,
            !!featureFlagSelector(getState(), "pendingInventory")
          );
          lock.throwIfAborted();
          await saveSyncDate(
            integrationId,
            "mercariLastSyncDateInventory",
            start.toISOString()
          );
        }

        lock.throwIfAborted();
        await db.collection("Plaid_Integrations").doc(integrationId).update({
          lastSync: start.toISOString(),
        });
        await updateIntegration(integrationId);
      }
    );

    if (!started) {
      notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Mercari");
    }
    return started;
  };
}

export function mercariSync(
  integration: MercariIntegration,
  dispatcher?: Dispatcher
) {
  return async (dispatch, getState) => {
    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Mercari",
      dispatcher,
      RequiredExtensionVersion
    );
    if (!extensionId) return;

    const inventoryType = userGetInventoryTypeSelector(getState());
    const shouldDisableInventory = inventoryType === "cash";

    const inventory =
      integration.inventory !== false && !shouldDisableInventory;

    setBeforeUnloadHandler("Mercari");

    try {
      const started = await dispatch(syncDaily(integration.id, inventory));
      if (started) dispatch(updateSyncStatus(undefined));
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      let code: ErrorSyncStatusCode = "unknown";
      let message: string = (e as Error).message || (e as Error).toString();

      if (e instanceof NotLoggedError) code = "user-not-logged-in";
      else if (e instanceof UnauthorizedError)
        message =
          "There was an authorization error. Please enter Mercari, log out and then log in.";

      dispatch(
        updateSyncStatus({
          type: "error",
          message,
          code,
          dispatcher,
        })
      );
    } finally {
      clearBeforeUnloadHandler("Mercari");
    }
  };
}

export function mercariSyncWithCheck(
  integrationId: string,
  noTimeCheck = false
) {
  return async (dispatch, getState) => {
    const state = getState();
    // Accountant / support impersonation sessions cannot trigger client-side syncs.
    if (isImpersonationSessionSelector(state)) return;
    const plan = userPlanSelector(state);

    if (plan === "ultimate") {
      const integration = getMercariIntegrations(state)?.find(
        ({ id }) => id === integrationId
      );

      if (
        integration &&
        integration.type === "mercari" &&
        integration.sync &&
        !integration.error
      ) {
        const shouldRun =
          noTimeCheck ||
          (!isSupportUserSessionSelector(state) &&
            (await shouldRunDailySync(integration.id, ReSyncInterval)));

        if (shouldRun) {
          await dispatch(
            mercariSync(integration, noTimeCheck ? "manual" : "auto")
          );
        }
      }
    }
  };
}

export function mercariSyncInventory(integrationId: string) {
  return async (
    dispatch,
    getState
  ): Promise<{ error?: true; success?: true }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "mercariInventory"))
      return { error: true };
    const inventoryType = userGetInventoryTypeSelector(getState());
    if (inventoryType === "cash") return { error: true };

    const integration = getMercariIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "mercari" || integration.error)
      return { error: true };

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Mercari",
      "manual",
      RequiredExtensionVersion
    );
    if (!extensionId) return { error: true };

    const client = new MercariClient();
    let cookieUserId;
    try {
      cookieUserId = (await client.getCredentials()).userId;
    } catch (e) {
      console.warn(e);
    }

    if (cookieUserId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Mercari with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }

    const initialize = await client.initialize();
    if (initialize?.user?.userId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to mercari with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["inventoryIsRunning" as const];
    setBeforeUnloadHandler("Mercari");

    const isInitialImport = checkIfItIsInitialImport(state, "mercariInventory");
    let lockBlocked = false;

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-inventory",
            })
          );
          const uid = await getUserId();
          const timestamp = Date.now();
          const filename = `${uid}/mercariInventory/${timestamp}_${integrationId}.json`;
          const upload = db.collection("Uploads").doc();
          await upload.set({
            user: uid,
            type: "inventory",
            provider: "mercari",
            filename,
            n: 0,
            timestamp,
            id: upload.id,
          });
          let docs = 0;
          const importedListingIds: string[] = [];

          const batch = new FirebaseBatcher();
          const cache = new Cacher<string, boolean>();
          const allItems: any[] = [];

          for await (const { sales: items } of client.userItemsQueryFull({
            status: "on_sale",
            sortBy: "created",
            sortType: "asc",
          })) {
            lock.throwIfAborted();
            allItems.push(...items);
            await parallelize(
              items,
              // eslint-disable-next-line no-loop-func
              async (item: (typeof items)[number]) => {
                lock.throwIfAborted();
                const product = await client.productQuery(item.id);
                if (product.status !== "on_sale") return;
                const inventory = mapInventory(product);
                const listingId = inventory.listing_id;

                if (listingId) {
                  const isEmpty = await cache.run(
                    listingId,
                    async (listingId) => {
                      const snapshot = await db
                        .collection("Inventory")
                        .where("user", "==", uid)
                        .where("listing_id", "==", listingId)
                        .get();
                      return snapshot.empty;
                    }
                  );

                  if (!isEmpty) return;
                }

                lock.throwIfAborted();
                const doc = db.collection("Inventory").doc();
                await batch.set(
                  doc,
                  stampNormalizedMatchFields({
                    ...inventory,
                    id: doc.id,
                    user: uid,
                    upload: upload.id,
                    import_type: isInitialImport
                      ? "initial-import"
                      : "direct-import",
                    import_platform: "mercari",
                    import_date: new Date(),
                  })
                );
                if (listingId) importedListingIds.push(listingId);
                docs++;
              },
              ParalelizeJobs
            );
          }

          lock.throwIfAborted();
          await batch.wait();
          await batch.commit();
          // Flag off: Pending_Inventory has nothing for this user and the
          // extra read must not be able to fail the import.
          if (
            importedListingIds.length &&
            !!featureFlagSelector(getState(), "pendingInventory")
          ) {
            await deletePendingInventoryByListingIds(uid, importedListingIds);
          }
          await uploadFile(filename, JSON.stringify(allItems));

          await upload.update({
            n: docs,
          });
        }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Mercari");
        return { error: true };
      }
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      toast.error(`Error: ${(e as Error).message}`);
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Mercari");
    }
    await updateIntegration(integrationId);
    return { success: true };
  };
}

async function mercariSyncSale(
  uid: string,
  cache: Cacher<string, boolean>,
  client: MercariClient,
  batch: FirebaseBatcher,
  startDate: Date,
  endDate: Date,
  sale: any,
  isInitialImport: boolean,
  uploadId: string,
  bankAccount: ReturnType<typeof bankAccountByIntegrationIdSelector>,
  lock: SyncLock,
  validOrderStates?: string[]
): Promise<number> {
  lock.throwIfAborted();
  let docs = 0;
  // Sales that are part of a bundle are also listed, we'll have to ignore them
  // Sales that are part of a bundle won't have this property
  // 2025-01-15: regular sales won't have the `transactionEvidence` property,
  //             but they will have the `activeOrder` one
  if (!sale.transactionEvidence && !sale.activeOrder) return docs;

  const updated = new Date(sale.updated * 1000);
  // Optimization:
  //   Always: sale_date <= updated,
  //   so if updated < startDate, then sale_date < startDate
  // This will prevent us making some requests
  if (updated < startDate) return docs;

  const [orderStatus, product] = await Promise.all([
    client.orderStatusConsolidated(sale.id),
    client.productQuery(sale.id).catch((e) => null),
  ]);

  sale.orderStatus = orderStatus;

  if (product) sale.product = product;

  if ((orderStatus?.orderState || "").startsWith("canceled")) return docs;

  if (
    validOrderStates &&
    validOrderStates.length > 0 &&
    !validOrderStates.includes(orderStatus.orderState)
  )
    return docs;

  if (sale.itemType === "bundle") {
    const orderContent = orderStatus?.componentContents?.find(
      (c) => c.type === "orderContent" && c.orderContent
    )?.orderContent;

    if (orderContent?.items?.length)
      sale.products = (
        await Promise.all(
          orderContent.items.map((i) =>
            client.productQuery(i.id).catch((e) => null)
          )
        )
      ).filter(Boolean);
  }

  const db = firestore();
  for (const transformed of mapSale(sale)) {
    if (!transformed.sale_date) return docs;

    if (transformed.sale_date < startDate || endDate < transformed.sale_date)
      return docs;

    const transactionId = transformed.transaction_id;
    if (transactionId) {
      const isEmpty = await cache.run(
        transactionId,
        async (transactionId: string) => {
          const snapshot = await db
            .collection("Sales")
            .where("user", "==", uid)
            .where("transaction_id", "==", transactionId)
            .get();
          return snapshot.empty;
        }
      );

      if (!isEmpty) return docs;
    }

    lock.throwIfAborted();
    const doc = db.collection("Sales").doc();

    const s = {
      ...transformed,
      id: doc.id,
      user: uid,
      upload: uploadId,
      import_type: isInitialImport ? "initial-import" : "direct-import",
      import_platform: "mercari",
      import_date: new Date(),
    };

    if (bankAccount) {
      s.sale_platform_account_id = bankAccount.id;
      s.sale_platform = bankAccount.name;
    }

    await batch.set(
      doc,
      finalizeSaleComputedFields({
        ...s,
      })
    );
    docs++;
  }

  return docs;
}

export function mercariSyncSales(
  integrationId: string,
  { start, end }: { start: MonthYear; end: MonthYear }
) {
  return async (
    dispatch,
    getState
  ): Promise<{ success?: boolean; error?: boolean }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "mercariSales"))
      return { error: true };
    const integration = getMercariIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "mercari" || integration.error)
      return { error: true };

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Mercari",
      "manual",
      RequiredExtensionVersion
    );
    if (!extensionId) return { error: true };

    const client = new MercariClient();
    let cookieUserId;
    try {
      cookieUserId = (await client.getCredentials()).userId;
    } catch (e) {
      console.warn(e);
    }

    if (cookieUserId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Mercari with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }

    const initialize = await client.initialize();
    if (initialize?.user?.userId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to mercari with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["salesIsRunning" as const];
    setBeforeUnloadHandler("Mercari");

    const isInitialImport = checkIfItIsInitialImport(state, "mercariSales");
    let lockBlocked = false;

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-sales",
            })
          );
          // Sync
          const uid = await getUserId();
          const timestamp = Date.now();
          const filename = `${uid}/mercariSales/${timestamp}_${integrationId}.json`;
          const upload = db.collection("Uploads").doc();
          await upload.set({
            user: uid,
            type: "sales",
            provider: "mercari",
            filename,
            n: 0,
            timestamp,
            id: upload.id,
          });
          let docs = 0;
          const bankAccount = bankAccountByIntegrationIdSelector(
            state,
            integrationId
          );

          const tz = effectiveUserTimeZoneSelector(getState());
          const startDate = startOfMonthInTimeZone(start.year, start.month, tz);
          const endDate = endOfMonthInTimeZone(end.year, end.month, tz);

          const batch = new FirebaseBatcher();
          const cache = new Cacher<string, boolean>();
          const allSales: any[] = [];

          for await (const { sales } of client.userItemsQueryFull()) {
            lock.throwIfAborted();
            allSales.push(...sales);
            await parallelize(
              sales,
              // eslint-disable-next-line no-loop-func
              async (sale: (typeof sales)[number]) => {
                docs += await mercariSyncSale(
                  uid,
                  cache,
                  client,
                  batch,
                  startDate,
                  endDate,
                  sale,
                  isInitialImport,
                  upload.id,
                  bankAccount,
                  lock,
                  CompleteOrderStates
                );
              },
              ParalelizeJobs
            );
          }

          for await (const { sales } of client.userItemsQueryFull({
            status: "trading",
          })) {
            lock.throwIfAborted();
            allSales.push(...sales);
            await parallelize(
              sales,
              // eslint-disable-next-line no-loop-func
              async (sale: (typeof sales)[number]) => {
                docs += await mercariSyncSale(
                  uid,
                  cache,
                  client,
                  batch,
                  startDate,
                  endDate,
                  sale,
                  isInitialImport,
                  upload.id,
                  bankAccount,
                  lock
                );
              },
              ParalelizeJobs
            );
          }

          lock.throwIfAborted();
          await batch.wait();
          await batch.commit();
          await uploadFile(filename, JSON.stringify(allSales));

          await upload.update({
            n: docs,
          });
        }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Mercari");
        return { error: true };
      }
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      toast.error(`Error: ${(e as Error).message}`);
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Mercari");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}
